import { useState } from 'preact/hooks';
import { Field, PageHead, Row, TextInput } from '../components/index.js';
import { LangSwitch } from '../components/LangSwitch.js';
import { num } from '../lib/format.js';
import { t, toLatinDigits, translateError } from '../lib/i18n.js';
import {
  changePin,
  logout,
  me,
  shopName,
  users,
  updateSettings,
  version,
  state,
  entries,
} from '../lib/khata.js';
import { openSheet, showToast } from '../lib/ui.js';
import { OfflineError } from '../lib/api.js';

export function Settings() {
  const [name, setName] = useState(shopName.value);
  const [pin, setPin] = useState('');
  const [newPin, setNewPin] = useState('');
  const [err, setErr] = useState('');
  const [pinErr, setPinErr] = useState('');

  const saveName = async (e: Event) => {
    e.preventDefault();
    setErr('');
    try {
      const r = await updateSettings({ shopName: name });
      if (!r.ok) return setErr(translateError(r.error));
      showToast(t('settings_nameSaved'));
    } catch (ex) {
      setErr(ex instanceof OfflineError ? t('form_offline') : t('settings_saveFailed'));
    }
  };

  const savePin = async (e: Event) => {
    e.preventDefault();
    setPinErr('');
    try {
      const r = await changePin(pin, newPin);
      if (!r.ok) return setPinErr(translateError(r.error));
      setPin('');
      setNewPin('');
      showToast(t('settings_pinChanged'));
    } catch (ex) {
      setPinErr(ex instanceof OfflineError ? t('form_offline') : t('settings_pinFailed'));
    }
  };

  return (
    <>
      <PageHead
        title={t('settings_title')}
        sub={t('settings_sub', { n: num(state.value.count), version: version.value || 'dev' })}
      />
      <section style={{ maxWidth: 420, marginBottom: 40 }}>
        <h6 class="section">{t('settings_language')}</h6>
        <LangSwitch />
      </section>
      {me.value && (
        <section style={{ maxWidth: 420, marginBottom: 40 }} data-testid="me">
          <h6 class="section">{t('settings_you')}</h6>
          <div>
            <strong>{me.value.name}</strong> <span class="muted">· {me.value.login}</span>
          </div>
        </section>
      )}
      <form onSubmit={saveName} style={{ maxWidth: 420, marginBottom: 40 }}>
        <Field label={t('settings_shopName')}>
          <TextInput value={name} onInput={setName} />
        </Field>
        {err && <div class="form-error">{err}</div>}
        <button type="submit" class="btn btn-secondary" style={{ marginTop: 10 }}>
          {t('settings_saveName')}
        </button>
      </form>
      <form onSubmit={savePin} style={{ maxWidth: 420, marginBottom: 40 }}>
        <h6 class="section">{t('settings_changePin')}</h6>
        <div class="grid-2">
          <Field label={t('settings_currentPin')}>
            <TextInput
              type="password"
              inputMode="numeric"
              value={pin}
              onInput={(v) => setPin(toLatinDigits(v).replace(/\D/g, '').slice(0, 8))}
              autoComplete="current-password"
            />
          </Field>
          <Field label={t('settings_newPin')}>
            <TextInput
              type="password"
              inputMode="numeric"
              value={newPin}
              onInput={(v) => setNewPin(toLatinDigits(v).replace(/\D/g, '').slice(0, 8))}
              autoComplete="new-password"
            />
          </Field>
        </div>
        {pinErr && <div class="form-error">{pinErr}</div>}
        <button
          type="submit"
          class="btn btn-secondary"
          style={{ marginTop: 10 }}
          disabled={pin.length < 4 || newPin.length < 4}
        >
          {t('settings_changePin')}
        </button>
      </form>
      {me.value?.role === 'owner' && (
        <section style={{ maxWidth: 520, marginBottom: 40 }}>
          <h6 class="section">{t('settings_users')}</h6>
          <div class="list" data-testid="users">
            {users.value.map((u) => (
              <Row
                key={u.id}
                title={u.name + (u.id === me.value?.id ? ` (${t('settings_youTag')})` : '')}
                sub={u.login}
                amount={u.active ? '' : t('user_disabled')}
                amountTone="muted"
                onClick={() => openSheet({ kind: 'user', id: u.id })}
              />
            ))}
          </div>
          <button
            type="button"
            class="btn btn-secondary"
            style={{ marginTop: 10 }}
            onClick={() => openSheet({ kind: 'user' })}
          >
            {t('settings_addUser')}
          </button>
        </section>
      )}
      <section style={{ maxWidth: 420, marginBottom: 40 }}>
        <h6 class="section">{t('settings_backup')}</h6>
        <p class="small muted">{t('settings_backupHelp', { n: num(entries.value.length) })}</p>
        <a class="btn btn-secondary" href="/api/export" download>
          {t('settings_download')}
        </a>
      </section>
      <section style={{ maxWidth: 420 }}>
        <h6 class="section">{t('settings_device')}</h6>
        <button type="button" class="btn btn-secondary btn-danger" onClick={() => void logout()}>
          {t('settings_logout')}
        </button>
      </section>
    </>
  );
}
